
/**
 * Difficulty mapping between IDs and display names
 * This ensures consistency across the application for difficulty handling
 */
export const DIFFICULTY_MAP = {
  // 1: 'Very Easy',
  2: 'Easy', 
  3: 'Medium',
  4: 'Hard',
  5: 'Very Hard'
};

/**
 * Get difficulty display name from ID
 * @param {number} id - Difficulty ID (1-5)
 * @returns {string} Display name for the difficulty
 */
export const getDifficultyDisplayName = (id) => {
  return DIFFICULTY_MAP[id] || 'Unknown';
};

/**
 * Get difficulty ID from display name
 * @param {string} name - Difficulty display name
 * @returns {number|null} Difficulty ID or null if not found
 */
export const getDifficultyId = (name) => {
  const entry = Object.entries(DIFFICULTY_MAP).find(([id, displayName]) => displayName === name);
  return entry ? parseInt(entry[0], 10) : null;
};

/**
 * Get all difficulty options as objects with id and name
 * @returns {Array} Array of {id, name} objects for all difficulties
 */
export const getAllDifficultyOptions = () => {
  return Object.entries(DIFFICULTY_MAP).map(([id, name]) => ({
    id: parseInt(id, 10),
    name: name
  }));
};

/**
 * Convert difficulty strings to IDs
 * @param {Array<string>} difficultyStrings - Array of difficulty strings
 * @returns {Array<number>} Array of difficulty IDs
 */
export const convertDifficultyStringsToIds = (difficultyStrings) => {
  if (!Array.isArray(difficultyStrings)) return [];
  if (difficultyStrings.includes('ALL')) return [];
  
  return difficultyStrings
    .map(str => getDifficultyId(str))
    .filter(id => id !== null);
};

/**
 * Convert difficulty IDs to strings
 * @param {Array<number>} difficultyIds - Array of difficulty IDs
 * @returns {Array<string>} Array of difficulty strings
 */
export const convertDifficultyIdsToStrings = (difficultyIds) => {
  if (!Array.isArray(difficultyIds)) return ['ALL'];
  if (difficultyIds.length === 0) return ['ALL'];
  
  return difficultyIds
    .map(id => getDifficultyDisplayName(id))
    .filter(name => name !== 'Unknown');
};
